import { useState } from 'react';
import { useT } from '../i18n/i18n';
import { AREA_LABEL, MEASURE_LABEL, createTree, type Area } from './api';
import PlannerDialog from './PlannerDialog';
import { TEMPLATES, localiseTemplate, type Template } from './templates';
import './extras.css';

function useAdds() {
  const t = useT();
  return (tpl: Template) => {
    const targets = tpl.targets.length;
    const activities = tpl.targets.reduce((n, x) => n + x.activities.length, 0);
    if (targets === 1 && activities === 1) return t('Adds a goal, 1 target and 1 activity.');
    if (targets === 1) return t('Adds a goal, 1 target and {activities} activities.', { activities });
    return t('Adds a goal, {targets} targets and {activities} activities.', { targets, activities });
  };
}

export default function TemplatePicker({ learnerId, onClose, onAdded }: { learnerId: string; onClose: () => void; onAdded: () => void }) {
  const t = useT();
  const adds = useAdds();
  const [area, setArea] = useState<Area | ''>('');
  const [chosen, setChosen] = useState<Template | null>(null);
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shown = TEMPLATES.filter((x) => !area || x.area === area);

  function choose(tpl: Template) {
    setChosen(tpl);
    setStatement(t(tpl.goal.statement));
    setError(null);
  }

  async function add() {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      await createTree(learnerId, localiseTemplate(chosen, t, statement.trim()));
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
      setBusy(false);
    }
  }

  if (chosen) {
    return (
      <PlannerDialog title={t(chosen.title)} onClose={onClose} className="planner-tpl-dialog">
        <p className="planner-tpl-desc">{t(chosen.description)}</p>
        <div className="field">
          <label htmlFor="tpl-goal-statement">{t('What we want')}</label>
          <textarea id="tpl-goal-statement" rows={3} maxLength={2000} value={statement} onChange={(e) => setStatement(e.target.value)} />
          <p className="field-hint">{t('Change the words so they fit your learner. You can edit everything else after it is added.')}</p>
        </div>
        <p className="planner-tpl-area">{t('Area of need: {area}', { area: t(AREA_LABEL[chosen.area]) })}</p>
        <h3 className="planner-tpl-heading">{t('Targets')}</h3>
        <ul className="planner-tpl-targets">
          {chosen.targets.map((x) => (
            <li key={x.target.statement}>
              <p className="planner-tpl-target">{t(x.target.statement)}</p>
              {x.target.successCriterion && <p className="planner-meta">{t('How we will know: {text}', { text: t(x.target.successCriterion) })}</p>}
              <ul className="planner-tpl-activities">
                {x.activities.map((a) => (
                  <li key={a.title}>
                    <strong>{t(a.title)}</strong>
                    <span className="planner-meta"> · {t(MEASURE_LABEL[a.measure])}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <p className="field-hint">{t('Each target starts with "Describe what happens now" in place of what happens now. Replace it once you have watched your learner.')}</p>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="btn" disabled={busy || !statement.trim()} onClick={() => void add()}>{busy ? t('Adding…') : t('Add to plan')}</button>
          <button type="button" className="btn secondary" disabled={busy} onClick={() => setChosen(null)}>{t('Back to templates')}</button>
        </div>
      </PlannerDialog>
    );
  }

  return (
    <PlannerDialog title={t('Start from a template')} onClose={onClose} className="planner-tpl-dialog">
      <p className="planner-tpl-desc">{t('Each template adds a goal with ready-written targets and activities. You can change any of it afterwards.')}</p>
      <div className="field planner-tpl-filter">
        <label htmlFor="tpl-area">{t('Area of need')}</label>
        <select id="tpl-area" value={area} onChange={(e) => setArea(e.target.value as Area | '')}>
          <option value="">{t('All areas')}</option>
          {(Object.keys(AREA_LABEL) as Area[]).filter((a) => TEMPLATES.some((x) => x.area === a)).map((a) => <option key={a} value={a}>{t(AREA_LABEL[a])}</option>)}
        </select>
      </div>
      <ul className="planner-tpl-list">
        {shown.map((x) => (
          <li key={x.id} className="planner-tpl-item">
            <div className="planner-tpl-text">
              <strong className="planner-tpl-title">{t(x.title)}</strong>
              <span>{t(x.description)}</span>
              <span className="planner-meta">{t(AREA_LABEL[x.area])} · {adds(x)}</span>
            </div>
            <button type="button" className="btn secondary" onClick={() => choose(x)} aria-label={t('Choose {name}', { name: t(x.title) })}>{t('Choose')}</button>
          </li>
        ))}
      </ul>
    </PlannerDialog>
  );
}
